// Thin wrapper around the shared `apiFetch` helper, hard-coding the
// `app: 'ads'` flag so each call lands on the ads sub-app mounted at
// `${LIGHTNING_API_BASE}/api/ads`.
//
// Mirrors `web/src/interior/api.ts` in spirit. The ads service exposes
// a smaller surface than interior (no batches, no /scans endpoint —
// the single submission endpoint is `POST /jobs` with the full wizard
// config) so this file is correspondingly leaner.

import { ADS_API_BASE, apiFetch } from '../utils/api';
import type {
  AdsConfigYaml,
  AdsContext,
  AdsHealth,
  AdsJobImagesResponse,
  CreateAdsJobRequest,
  CreateAdsJobResponse,
  DateRange,
  HistoryResponse,
  Job,
  LogoUploadResponse,
} from './types';

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.detail) detail = body.detail;
    } catch {
      // body wasn't JSON; keep statusText
    }
    throw new Error(detail);
  }
  return (await res.json()) as T;
}

async function emptyOrThrow(res: Response): Promise<void> {
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.detail) detail = body.detail;
    } catch {
      // ignore
    }
    throw new Error(detail);
  }
}

// ----- /context -----------------------------------------------------------

export async function fetchAdsContext(scanId: string): Promise<AdsContext> {
  const res = await apiFetch(`/context/${encodeURIComponent(scanId)}`, {
    app: 'ads',
  });
  return jsonOrThrow<AdsContext>(res);
}

// ----- /jobs --------------------------------------------------------------

export async function uploadLogo(file: File): Promise<LogoUploadResponse> {
  const form = new FormData();
  form.append('file', file);
  const res = await apiFetch('/jobs/logo', {
    app: 'ads',
    method: 'POST',
    body: form,
  });
  return jsonOrThrow<LogoUploadResponse>(res);
}

export async function createAdsJob(
  body: CreateAdsJobRequest,
): Promise<CreateAdsJobResponse> {
  const res = await apiFetch('/jobs', {
    app: 'ads',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return jsonOrThrow<CreateAdsJobResponse>(res);
}

// ----- /api/jobs/* --------------------------------------------------------

export async function listLiveJobs(): Promise<Job[]> {
  const res = await apiFetch('/api/jobs/live', { app: 'ads' });
  return jsonOrThrow<Job[]>(res);
}

export async function listHistoryJobs(
  dateRange: DateRange,
  status: string | null,
  page: number,
  pageSize: number,
  scanId?: string | null,
  organizationId?: string | null,
): Promise<HistoryResponse> {
  const params = new URLSearchParams();
  if (dateRange.from) params.set('from_date', dateRange.from.toISOString());
  if (dateRange.to) params.set('to_date', dateRange.to.toISOString());
  if (status) params.set('status', status);
  if (scanId) params.set('scan_id', scanId);
  if (organizationId) params.set('organization_id', organizationId);
  params.set('page', String(page));
  params.set('page_size', String(pageSize));
  const res = await apiFetch(`/api/jobs/history?${params.toString()}`, {
    app: 'ads',
  });
  return jsonOrThrow<HistoryResponse>(res);
}

export async function getJob(jobId: string): Promise<Job> {
  const res = await apiFetch(`/api/jobs/${encodeURIComponent(jobId)}`, {
    app: 'ads',
  });
  return jsonOrThrow<Job>(res);
}

export async function abortJob(jobId: string): Promise<void> {
  const res = await apiFetch(`/api/jobs/${encodeURIComponent(jobId)}/abort`, {
    app: 'ads',
    method: 'POST',
  });
  return emptyOrThrow(res);
}

export async function rerunJob(jobId: string): Promise<CreateAdsJobResponse> {
  const res = await apiFetch(`/api/jobs/${encodeURIComponent(jobId)}/rerun`, {
    app: 'ads',
    method: 'POST',
  });
  return jsonOrThrow<CreateAdsJobResponse>(res);
}

export async function deleteJob(jobId: string): Promise<void> {
  const res = await apiFetch(`/api/jobs/${encodeURIComponent(jobId)}`, {
    app: 'ads',
    method: 'DELETE',
  });
  return emptyOrThrow(res);
}

export async function deleteHistoryJob(jobId: string): Promise<void> {
  const res = await apiFetch(
    `/api/jobs/history/${encodeURIComponent(jobId)}`,
    { app: 'ads', method: 'DELETE' },
  );
  return emptyOrThrow(res);
}

export async function abortAllLive(): Promise<void> {
  const res = await apiFetch('/api/jobs/live', {
    app: 'ads',
    method: 'DELETE',
  });
  return emptyOrThrow(res);
}

export async function fetchAdsJobImages(
  jobId: string,
): Promise<AdsJobImagesResponse> {
  const res = await apiFetch(
    `/api/jobs/${encodeURIComponent(jobId)}/images`,
    { app: 'ads' },
  );
  return jsonOrThrow<AdsJobImagesResponse>(res);
}

// ----- /health + /api/settings -------------------------------------------

export async function fetchAdsHealth(): Promise<AdsHealth> {
  const res = await apiFetch('/health', { app: 'ads' });
  return jsonOrThrow<AdsHealth>(res);
}

export async function fetchAdsConfigYaml(): Promise<AdsConfigYaml> {
  const res = await apiFetch('/api/settings/config-yaml', { app: 'ads' });
  return jsonOrThrow<AdsConfigYaml>(res);
}

export { ADS_API_BASE };
